import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Globe2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { api } from "../api";

/**
 * Searchable dropdown of ALL ISO-3166 countries. Emits the ISO alpha-2 code.
 *
 * Props:
 *   value: ISO alpha-2 code (or "")
 *   onChange: (code: string) => void
 *   placeholder: label when empty
 *   disabledCodes?: string[]  // codes already in use (admin add-form)
 */
export default function CountryCombobox({ value, onChange, placeholder = "Select country…", testid = "country-combobox", disabledCodes = [] }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/iso-countries").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const byCode = useMemo(() => Object.fromEntries(items.map((c) => [c.code, c])), [items]);
  const disabledSet = useMemo(() => new Set(disabledCodes), [disabledCodes]);
  const selected = value ? byCode[value] : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full h-10 inline-flex items-center justify-between gap-2 rounded-md border border-[#E2E4E0] bg-white px-3 text-sm text-[#1A1D1A] hover:bg-[#F9F9F8]"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Globe2 size={14} className="text-[#5C665A] shrink-0" />
            <span className="truncate">
              {selected ? (
                <>
                  <span className="db-mono text-[#5C665A] mr-1.5">{selected.code}</span>
                  {selected.name}
                </>
              ) : (
                <span className="text-[#8A9088]">{placeholder}</span>
              )}
            </span>
          </span>
          <ChevronsUpDown size={14} className="text-[#5C665A] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[300px]" align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Search country or code…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No country matches.</CommandEmpty>
            <CommandGroup>
              {items.map((c) => {
                const isDisabled = disabledSet.has(c.code);
                return (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.code}`}
                    disabled={isDisabled}
                    onSelect={() => { if (!isDisabled) { onChange(c.code); setOpen(false); } }}
                    data-testid={`${testid}-opt-${c.code}`}
                    className={isDisabled ? "opacity-40 pointer-events-none" : ""}
                  >
                    <span className="db-mono text-[11px] w-8 text-[#5C665A]">{c.code}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    {value === c.code && <Check size={14} className="text-[#2D6A4F]" />}
                    {isDisabled && <span className="text-[10px] text-[#5C665A]">(added)</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
